import { useId, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { CORE_NAV, navPath, point, ReactorDefs, ReactorRings, ReactorEngine, ReactorNavigation, EmergencyArc, ReactorReadouts } from "./ReactorAssembly";
import {
  Power, Sun, Activity, MessageSquare, Bot, Command, Terminal, Settings, Mic, X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const THEME = {
  idle: { ring: "#18c9ff", glow: "#82eeff", label: "IDLE" },
  listening: { ring: "#56d4ff", glow: "#b7eaff", label: "LISTENING" },
  thinking: { ring: "#087fff", glow: "#74cfff", label: "THINKING" },
  speaking: { ring: "#45bbff", glow: "#d4f9ff", label: "SPEAKING" },
  executing: { ring: "#18c9ff", glow: "#9adeff", label: "EXECUTING" },
  alert: { ring: "#ff3f58", glow: "#ff8a97", label: "EMERGENCY" },
};
const STATE_COLORS = { idle: "#52d9ff", listening: "#56d4ff", thinking: "#087fff", speaking: "#b5eaff", executing: "#8deaff" };

export function ReactorCore({
  state = "idle", emergency = false, health,
  onNavigate, onQuick, onEmergencyToggle, onMic, micOn = false, taskLabel,
}) {
  const [open, setOpen] = useState(false);
  const id = `reactor-${useId().replace(/:/g, "")}`;
  const reducedMotion = useReducedMotion();
  const toggleRef = useRef(null);
  const effState = emergency ? "alert" : state;
  const t = THEME[effState] || THEME.idle;
  const close = () => { setOpen(false); toggleRef.current?.focus(); };

  const revealBtns = [
    { icon: Sun, label: "Briefing", angle: -90, action: () => onQuick?.("briefing") },
    { icon: Activity, label: "Status", angle: -45, action: () => onQuick?.("status") },
    { icon: Bot, label: "Agents", angle: 0, action: () => onNavigate?.("/agents") },
    { icon: Command, label: "Commands", angle: 45, action: () => onNavigate?.("/commands") },
    { icon: Terminal, label: "Terminal", angle: 90, action: () => onNavigate?.("/terminal") },
    { icon: Settings, label: "Settings", angle: 135, action: () => onNavigate?.("/settings") },
    { icon: MessageSquare, label: "Chat", angle: 180, action: () => onQuick?.("focus") },
    { icon: Power, label: emergency ? "Restore" : "Stop", angle: 225, danger: true, action: () => onEmergencyToggle?.() },
  ];

  return (
    <div className={`reactor-layout reactor-state-${effState}`} data-testid="reactor-core" data-state={effState}
      style={{ "--reactor-ring": t.ring, "--reactor-glow": t.glow }}
      onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); close(); } }}>
      <div className="reactor-stage">
        <svg viewBox="0 0 820 800" className="reactor-svg" aria-label={`Jarvis interactive reactor — ${t.label}`} role="group" data-testid="reactor-svg">
          <ReactorDefs id={id} theme={t} />
          <ReactorRings id={id} />
          <ReactorEngine id={id} open={open} />
          <ReactorNavigation onNavigate={onNavigate} />
          <ReactorReadouts id={id} health={health} emergency={emergency} state={effState} taskLabel={taskLabel} />
          <g ref={toggleRef} role="button" tabIndex={0} data-testid="core-toggle" className="reactor-toggle"
            aria-label={open ? "Close Jarvis core controls" : "Open Jarvis core controls"} aria-expanded={open}
            onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}>
            <title>Open Jarvis core controls</title><circle cx="410" cy="400" r="181" fill="transparent" />
          </g>
          <AnimatePresence>
            {open && <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reducedMotion ? 0 : .2 }} data-testid="core-quick-controls">
              <circle cx="410" cy="400" r="185" fill="#020c16" fillOpacity=".93" stroke="#75ddff" strokeWidth="1" />
              {revealBtns.map((b, i) => {
                const [x, y] = point(140, b.angle);
                return <motion.g key={b.label} initial={reducedMotion ? false : { x: 410 - x, y: 400 - y, opacity: 0 }} animate={{ x: 0, y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 230, damping: 22, delay: reducedMotion ? 0 : i * .025 }}>
                  <g role="button" tabIndex={0} aria-label={b.label} className={`reactor-quick ${b.danger ? "reactor-quick-danger" : ""}`}
                    data-testid={`core-quick-${b.label.toLowerCase()}`} onClick={() => { close(); b.action?.(); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); close(); b.action?.(); } }}>
                    <circle cx={x} cy={y} r="36" /><b.icon x={x - 10} y={y - 19} width="20" height="20" />
                    <text x={x} y={y + 17} textAnchor="middle" className="reactor-labels" fontSize="12" fill="currentColor">{b.label.toUpperCase()}</text>
                  </g>
                </motion.g>;
              })}
              <g role="button" tabIndex={0} aria-label="Close core controls" data-testid="core-reveal-close" className="reactor-quick" onClick={close}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); close(); } }}>
                <circle cx="410" cy="400" r="28" /><X x="400" y="390" width="20" height="20" />
              </g>
            </motion.g>}
          </AnimatePresence>
          <EmergencyArc id={id} emergency={emergency} onActivate={() => { setOpen(false); onEmergencyToggle?.(); }} />
        </svg>
      </div>
      <select className="reactor-mobile-nav" data-testid="core-mobile-navigation" aria-label="Navigate to section" value=""
        onChange={(e) => onNavigate?.(e.target.value)}>
        <option value="" disabled>Navigate to section</option>
        {Object.values(CORE_NAV).flat().map((label) => <option key={label} value={navPath(label)}>{label}</option>)}
      </select>

      {/* Voice control strip (separate chamfered panel below the reactor) */}
      <div className="w-full max-w-[680px] mx-auto shrink-0">
        <div className="jv-panel hud-chamfer flex items-center justify-between gap-3 px-4 py-2.5" data-testid="voice-strip">
          {/* left legend: idle/thinking */}
          <div className="hidden sm:flex items-center gap-3">
            {["idle", "listening", "thinking"].map((s) => (
              <StateLegend key={s} s={s} active={effState === s} />
            ))}
          </div>
          {/* left mirrored waveform */}
          <Waveform color={t.glow} active={effState !== "idle"} side="left" />
          {/* mic */}
          <button onClick={onMic} data-testid="core-mic-btn" aria-label="Toggle clap-to-wake microphone" aria-pressed={micOn} title="Clap-to-wake microphone. Speech transcription is not connected."
            className={cn("shrink-0 w-16 h-16 rounded-full border-2 flex items-center justify-center transition-colors", 
              micOn
                ? "border-jarvis-amber text-jarvis-amber bg-jarvis-amber/15 shadow-[0_0_20px_rgba(255,211,78,0.5)]"
                : "border-jarvis-cyan text-jarvis-cyanBright bg-jarvis-cyan/10 hover:bg-jarvis-cyan/20 shadow-[0_0_16px_rgba(24,201,255,0.4)]")}>
            <Mic className="w-6 h-6" />
          </button>
          {/* right mirrored waveform */}
          <Waveform color={t.glow} active={effState !== "idle"} side="right" />
          {/* right legend: speaking/executing */}
          <div className="hidden sm:flex items-center gap-3">
            {["speaking", "executing"].map((s) => (
              <StateLegend key={s} s={s} active={effState === s} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StateLegend({ s, active }) {
  const c = STATE_COLORS[s];
  return (
    <span className="flex items-center gap-1.5 font-brand text-[9px] font-semibold uppercase tracking-[0.14em]"
      data-testid={`core-state-${s}`}
      style={{ color: active ? c : "#3d5a68" }}>
      <span className="w-2 h-2 rounded-full transition-all" style={{
        background: active ? c : "#1c3341",
        boxShadow: active ? `0 0 10px ${c}` : "none",
      }} />
      {s}
    </span>
  );
}

function Waveform({ color, active, side }) {
  const bars = side === "left" ? [10, 16, 24, 34, 22, 14] : [14, 22, 34, 24, 16, 10];
  return (
    <div className="flex-1 flex items-center justify-center gap-[3px] h-8 min-w-0" aria-hidden>
      {bars.map((h, i) => (
        <span key={h} className={cn("w-[3px] rounded-full", active && "jv-breathe-core")}
          style={{
            height: active ? h : 5,
            background: color,
            opacity: active ? 0.85 : 0.28,
            animationDelay: `${i * 0.12}s`,
            transition: "height .3s ease, opacity .3s ease",
          }} />
      ))}
    </div>
  );
}
